import React, { useState, useRef, useEffect } from "react";
import PostCard from "./PostCard";
import SearchArea from "./SearchArea";
import "../styles/styles.css";
import axios from "axios";
import { faGear, faTrash, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import {
  faChevronDown,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

const ContentArea = ({
  columns,
  isLoading,
  handleSearch,
  handleAddColumn,
  fetchData,
  handleFocus,
  focusedPostId,
  searchKeyword,
  toggleTitleEdit,
  handleBlur,
  setColumns,
  handleLoadMore,
  loading,
  hasMore,
  apihandleSearch,
}) => {
  const [segregatedColumns, setSegregatedColumns] = useState({});
  // Refs for each column to manipulate the DOM directly
  const columnRefs = useRef([]);
  const [searchQueries, setSearchQueries] = useState({});

  const [searching, setSearching] = useState(false);

  const [subColumnSearchInputs, setSubColumnSearchInputs] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [activeSubcolumns, setActiveSubcolumns] = useState({});

  const platformColors = {
    facebook: "#1877F2",
    twitter: "#000000",
    instagram: "#E4405F",
    tiktok: "#000000",
    linkedin: "#0077B5",
    youtube: "#FF0000",
  };

  const handleToggleSubcolumn = (subIndex) => {
    setActiveSubcolumns((prev) => ({
      ...prev,
      [subIndex]: !prev[subIndex], // Toggle the subcolumn's active state
    }));
  };

  const handleExpandColumn = async (index, selectedPlatforms) => {
    setSegregatedColumns((prevState) => {
      const newState = { ...prevState };

      if (!selectedPlatforms.length) {
        delete newState[index]; // Collapse column if no platforms are selected
        localStorage.removeItem(`subColumns-${index}`); // Remove saved subcolumns
        return newState;
      }

      // Ensure posts are available
      if (!columns[index] || !columns[index].posts) {
        console.error(`No posts found for column at index ${index}`);
        return prevState;
      }

      // Filter valid platforms
      const validPlatforms = selectedPlatforms.filter((platform) =>
        columns[index].posts.some(
          (post) => post.platform.toLowerCase() === platform.toLowerCase()
        )
      );

      // if (validPlatforms.length === 0) {
      //   alert("No posts available for the selected platforms.");
      //   return prevState;
      // }

      // Create subcolumns
      const newSubColumns = validPlatforms.map((platform, subIndex) => {
        const platformPosts = columns[index].posts.filter(
          (post) => post.platform.toLowerCase() === platform.toLowerCase()
        );

        return {
          keyword: platform,
          posts: [...platformPosts],
          initialPosts: [...platformPosts],
          uniqueId: `column-${index}-sub-${subIndex}`,
          parentIndex: index,
          searchInput: "",
        };
      });

      newState[index] = newSubColumns;

      // Save subcolumns in localStorage
      localStorage.setItem(
        `subColumns-${index}`,
        JSON.stringify(newSubColumns)
      );

      return newState;
    });
  };

  const restoreStateFromLocalStorage = async () => {
    let restoredColumns = [];

    // Step 1: Restore columns from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (
        key.startsWith("searchInput-") ||
        key.startsWith("selectedPlatforms-") ||
        key.startsWith("fromDate-") ||
        key.startsWith("toDate-")
      ) {
        const columnIndex = parseInt(key.split("-")[1], 10);

        if (!restoredColumns[columnIndex]) {
          restoredColumns[columnIndex] = {
            keyword: "Untitled Column",
            posts: [],
            initialPosts: [],
            isExpanded: true,
            searchKeyword: "",
            editableTitle: false,
            selectedPlatforms: [],
            fromDate: "", // Initialize fromDate
            toDate: "", // Initialize toDate
          };
        }

        // Restoring search input
        if (key.startsWith("searchInput-")) {
          restoredColumns[columnIndex].searchKeyword =
            localStorage.getItem(key);
        }
        // Restoring selected platforms
        else if (key.startsWith("selectedPlatforms-")) {
          restoredColumns[columnIndex].selectedPlatforms = JSON.parse(
            localStorage.getItem(key)
          );
        }
        // Restoring fromDate
        else if (key.startsWith("fromDate-")) {
          restoredColumns[columnIndex].fromDate = localStorage.getItem(key);
        }
        // Restoring toDate
        else if (key.startsWith("toDate-")) {
          restoredColumns[columnIndex].toDate = localStorage.getItem(key);
        }
      }
    }

    // Step 2: Filter out undefined columns and set them in state
    const filteredColumns = restoredColumns.filter(Boolean);
    setColumns(filteredColumns);

    // Step 3: Fetch posts for each column before expanding subcolumns
    // for (let index = 0; index < filteredColumns.length; index++) {
    //   const column = filteredColumns[index];

    //   if (column.searchKeyword) {
    //     console.log(`Fetching posts for column ${index} with keyword: ${column.searchKeyword}`);
    //     await fetchData(index, 1, column.searchKeyword, column.fromDate, column.toDate, column.selectedPlatforms); // Wait until posts are fetched with dates
    //   }
    // }

    // Step 4: Restore subcolumns **after posts are loaded**
    setTimeout(async () => {
      for (let index = 0; index < filteredColumns.length; index++) {
        const column = filteredColumns[index];

        if (column.selectedPlatforms.length > 0) {
          console.log(
            `Expanding subcolumns for column ${index}:`,
            column.selectedPlatforms
          );
          await handleExpandColumn(index, column.selectedPlatforms);
        }

        // Restore subcolumns from localStorage
        const savedSubColumns = localStorage.getItem(`subColumns-${index}`);
        if (savedSubColumns) {
          setSegregatedColumns((prevState) => ({
            ...prevState,
            [index]: JSON.parse(savedSubColumns),
          }));
        }
      }
    }); // Delay to ensure posts are loaded
  };

  useEffect(() => {
    restoreStateFromLocalStorage();
  }, []); // Empty dependency array ensures it runs only once

  const handleSubColumnSearch = (mainIndex, subIndex, searchValue) => {
    // Update the search input state for the specific subcolumn
    setSubColumnSearchInputs((prevState) => ({
      ...prevState,
      [`${mainIndex}-${subIndex}`]: searchValue, // Store the search value uniquely per column and subcolumn
    }));

    // Filter posts based on search for the specific subcolumn
    setSegregatedColumns((prevState) => {
      if (!prevState[mainIndex]) return prevState; // Ensure the main column exists

      return {
        ...prevState,
        [mainIndex]: prevState[mainIndex].map((subCol, idx) => {
          if (idx !== subIndex) return subCol; // Skip if it's not the subcolumn being searched

          if (!subCol.initialPosts) {
            console.error("❌ No Initial Posts Found for Filtering", subCol);
            return subCol; // If no initial posts, just return the subcolumn as is
          }

          console.log(
            "✅ Initial Posts Before Filtering:",
            subCol.initialPosts
          );

          const parsedConditions = parseSearchQuery(searchValue); // Parse the search query (AND/OR logic)

          // Filter the posts for this specific subcolumn based on the search query
          const filteredPosts = subCol.initialPosts.filter((post) => {
            const text = post.text ? post.text.trim().toLowerCase() : ""; // Normalize the text

            return parsedConditions.every((condition) => {
              const regex = new RegExp(
                `(^|[\\s#])(${condition.keywords
                  .map((word) => word.replace(/[.*+?^=!:${}()|/\\]/g, "\\$&"))
                  .join("|")})(?=$|[\\s#])`,
                "i"
              );

              if (condition.operator === "AND") {
                // Apply AND logic (all keywords must match)
                return condition.keywords.every((word) => regex.test(text));
              } else if (condition.operator === "OR") {
                // Apply OR logic (any keyword should match)
                return condition.keywords.some((word) => regex.test(text));
              }

              return false; // Default to false for unrecognized operators
            });
          });

          // Return the updated subcolumn with filtered posts and highlighted text
          return {
            ...subCol,
            posts: filteredPosts.map((post) => ({
              ...post,
              highlightedText: highlightText(
                post.text,
                parsedConditions.flatMap((condition) => condition.keywords)
              ),
            })),
            searchKeyword: searchValue, // Store the search keyword for highlighting purposes
          };
        }),
      };
    });
  };

  const parseSearchQuery = (query) => {
    if (!query || typeof query !== "string") return [];

    // Regex to extract phrases in quotes and individual words split by spaces or commas
    const keywordPattern = /"([^"]+)"|([^+\s,]+)/g;
    const keywords = [];
    let match;

    while ((match = keywordPattern.exec(query)) !== null) {
      if (match[1]) {
        // Multi-word phrase in quotes
        keywords.push(match[1].trim());
      } else if (match[2]) {
        // Single word
        keywords.push(match[2].trim());
      }
    }

    let conditions = [];
    let currentCondition = [];
    let operator = "OR"; // Default operator

    keywords.forEach((keyword) => {
      const normalizedKeyword = keyword.trim().toUpperCase();

      if (
        normalizedKeyword === "AND" ||
        normalizedKeyword === "OR" ||
        normalizedKeyword === "+"
      ) {
        // If an operator is found, store the current condition and set the new operator
        if (currentCondition.length) {
          conditions.push({ operator, keywords: currentCondition });
          currentCondition = [];
        }
        operator = normalizedKeyword === "+" ? "OR" : normalizedKeyword; // Treat + as OR
      } else {
        currentCondition.push(keyword);
      }
    });

    if (currentCondition.length) {
      conditions.push({ operator, keywords: currentCondition });
    }

    return conditions;
  };
  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(
      (prev) =>
        prev.includes(platform)
          ? prev.filter((p) => p !== platform) // Remove if already selected
          : [...prev, platform] // Add if not selected
    );
  };

  // Function to render posts
  const renderPosts = (column, mainIndex, subIndex = null) => {
    if (!Array.isArray(column.posts)) {
      return <div className="loading-message">Loading...</div>;
    }
    if (column.loading)
      return <div className="loading-message">Loading...</div>;
    if (column.error)
      return <div className="error-message">Error: {column.error}</div>;
    if (!column.posts.length)
      return (
        <div className="no-posts-container">
          <div className="no-posts-content">
            <div className="no-posts-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-search"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h3 className="no-posts-title">No Posts Found</h3>
            <p className="no-posts-description">
              Try adjusting your search or filters to find what you're looking
              for.
            </p>
          </div>
        </div>
      );

    // Get the search keyword for this subcolumn
    const searchKeyword = column.searchKeyword ? [column.searchKeyword] : [];

    return column.posts.map((post, postIndex) => (
      <PostCard
        key={`post-${mainIndex}-${subIndex !== null ? `${subIndex}-` : ""}${
          post.id
        }`}
        post={{
          ...post,
          text: highlightText(post.text, searchKeyword), // Apply highlighting
        }}
        onFocus={handleFocus}
        focusedPostId={focusedPostId}
        searchKeyword={searchKeyword}
        className={`post-card-${mainIndex}${
          subIndex !== null ? `-of-${subIndex}` : ""
        }-of-${postIndex}`}
        id={`post-card-${mainIndex}${
          subIndex !== null ? `-of-${subIndex}` : ""
        }-of-${postIndex}`}
      />
    ));
  };

  const highlightText = (text, keywords) => {
    if (!keywords || keywords.length === 0 || !text) return text;

    // Flatten keywords by splitting on " + " or any delimiter
    const splitKeywords = keywords.flatMap(
      (keyword) => keyword.split(/\s*\+\s*/).map((k) => k.trim()) // Adjust to split on " + " with spaces
    );

    const regex = new RegExp(
      `(${splitKeywords
        .map((keyword) => keyword.replace(/[.*+?^=!:${}()|/\\]/g, "\\$&"))
        .join("|")})`,
      "gi"
    );

    return text.split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleUpdateColumnName = (index, newName) => {
    setColumns((prevColumns) =>
      prevColumns.map((column, colIndex) =>
        colIndex === index ? { ...column, keyword: newName } : column
      )
    );
  };

  useEffect(() => {
    const closeMenu = (e) => {
      if (!e.target.closest(".header-menu-container")) {
        setMenuOpenIndex(null);
      }
    };
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  // const apihandleSearch = async (searchQuery, index) => {
  //   if (!searchQuery.trim() || selectedPlatforms.length === 0) {
  //     alert("Please enter a keyword and select at least one platform.");
  //     return;
  //   }

  //   setSearching(true); // Disable input and button while searching

  //   try {
  //     const response = await axios.post("api/keyword/", {
  //       keyword: searchQuery,
  //       platforms: selectedPlatforms,
  //       columnIndex: index, // Pass the column index to the API if necessary
  //     });

  //     console.log("Search Results for column", index, ":", response.data);

  //     // Handle the search results for this specific column
  //   } catch (error) {
  //     console.error("Error fetching search results:", error);
  //   } finally {
  //     setSearching(false); // Re-enable input and button after request completes
  //     window.location.reload();
  //   }
  // };

  return (
    <div className="content-wrapper">
  <div className="content">
    {columns.map((column, index) => {
      const mainColumn = {
        ...column,
        type: "main",
        index: index,
        className: `vertical-card main-column column-${index}`,
      };

      const subColumns = segregatedColumns[index] || [];

      const combinedColumns = [mainColumn, ...subColumns].map((col, colIndex) => ({
        ...col,
        type: col.type || "sub",
        renderIndex: colIndex,
        uniqueId: col.uniqueId || `column-${index}-${colIndex}`,
      }));

      return (
        <div
          key={`deck-column-${index}`}
          className={`deck-column-${index}`}
          ref={(el) => (columnRefs.current[index] = el)} // Store reference for deletion
          style={{ display: "flex", gap: "10px" }}
        >
          {combinedColumns.map((column, colIndex) => {
            const isMainColumn = column.type === "main";
            const columnClassName = isMainColumn
              ? `vertical-card main-column column-${index}`
              : `sub-column ${column.uniqueId}`;

            return (
              <div
                key={column.uniqueId}
                className={columnClassName}
                id={columnClassName}
                style={{ marginTop: isMainColumn ? "0" : "2%" }}
              >
                <div
                  className="header-card"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    width: "90%",
                    padding: "15px",
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div className="header-content" style={{ flex: 1, textAlign: "center" }}>
                    {isMainColumn ? (
                      column.editableTitle ? (
                        <input
                          type="text"
                          value={column.keyword || ""}
                          onChange={(e) => {
                            setColumns((prev) =>
                              prev.map((col, idx) =>
                                idx === index ? { ...col, keyword: e.target.value } : col
                              )
                            );
                          }}
                          onBlur={() => {
                            handleBlur(index, column.keyword ? column.keyword.trim() : "");
                          }}
                          className={`header-title-input column-${index}`}
                          autoFocus
                          placeholder="Enter Column Title"
                          style={{
                            textAlign: "center",
                            width: "100%",
                            fontSize: "18px",
                            fontWeight: "bold",
                            border: "none",
                            outline: "none",
                          }}
                        />
                      ) : (
                        <h3
                          onClick={() => toggleTitleEdit(index)}
                          style={{ margin: "0", fontSize: "18px", fontWeight: "bold" }}
                        >
                          {column.keyword || "Untitled Column"}
                        </h3>
                      )
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          borderRadius: "12px",
                          backgroundColor: column.keyword
                            ? platformColors[column.keyword.toLowerCase()]
                            : "#ddd",
                          color: "#fff",
                          fontSize: "16px",
                          fontWeight: "bold",
                          textTransform: "capitalize",
                        }}
                      >
                        <img
                          src={`/icons/${column.keyword ? column.keyword.toLowerCase() : "default"}.png`}
                          alt={column.keyword || "default"}
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            backgroundColor: "#fff",
                            padding: "2px",
                          }}
                        />
                        <span>{column.keyword || "Untitled Column"}</span>
                      </div>
                    )}
                  </div>
                </div>

                {isMainColumn && (
                  <SearchArea
                    columnIndex={index}
                    keyword={column.keyword}
                    onSearch={handleSearch}
                    onExpand={handleExpandColumn}
                    isExpanded={!!segregatedColumns[index]}
                    onUpdateColumnName={handleUpdateColumnName}
                    apihandleSearch={apihandleSearch}
                    setColumns={setColumns}
                    columns={columns}
                    segregatedColumns={segregatedColumns}
                    setSegregatedColumns={setSegregatedColumns}
                    activeSubcolumns={activeSubcolumns}
                    setActiveSubcolumns={setActiveSubcolumns}
                  />
                )}

                {!isMainColumn && (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "90%",
                    }}
                  >
                    <input
                      type="text"
                      className="sub-column-search-input"
                      value={subColumnSearchInputs[`${index}-${colIndex - 1}`] || ""}
                      onChange={(e) =>
                        handleSubColumnSearch(index, colIndex - 1, e.target.value) // Update search for this specific subcolumn
                      }
                      placeholder={`Search within ${column.keyword}`}
                      style={{
                        width: "100%",
                        padding: "8px 35px 8px 8px", // Adjust padding for better spacing
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        marginBottom: "10px",
                      }}
                    />
                    {/* Help Icon */}
                    <i
                      className="fas fa-question-circle"
                      title="Click for more information"
                      onClick={() => setIsModalOpen((prev) => !prev)} // Toggle info box visibility
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "47%",
                        transform: "translateY(-50%)",
                        color: "#007BFF",
                        cursor: "pointer",
                        fontSize: "16px",
                        border: "2px solid #007BFF",
                        borderRadius: "50%",
                        padding: "2px",
                      }}
                    ></i>
                  </div>
                )}

                {/* Info Box */}
                {isModalOpen && (
                  <div
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "35px",
                      background: "#fff",
                      padding: "12px",
                      borderRadius: "8px",
                      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                      fontSize: "13px",
                      color: "#333",
                      width: "270px",
                      zIndex: 100,
                      textAlign: "left",
                      border: "1px solid #ddd",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: "#007BFF",
                        marginBottom: "5px",
                        borderBottom: "1px solid #ddd",
                        paddingBottom: "5px",
                      }}
                    >
                      🔍 Search Tips
                    </div>
                    <ul
                      style={{
                        paddingLeft: "18px",
                        margin: "0",
                        listStyleType: "disc",
                        lineHeight: "1.5",
                      }}
                    >
                      <li>Use **keywords** to filter results.</li>
                      <li>Separate multiple words with **spaces**.</li>
                      <li>Supports **AND / OR** for better filtering.</li>
                      <li>Special characters might not be supported.</li>
                    </ul>

                    {/* Subcolumn Toggle Button */}
                    {segregatedColumns?.[index] &&
                      segregatedColumns[index].map((subCol, subIndex) => {
                        const isSubcolumnActive = activeSubcolumns[subIndex];
                        return (
                          <div key={subCol.uniqueId} style={{ display: isSubcolumnActive ? "block" : "none" }}>
                            <button onClick={() => handleToggleSubcolumn(subIndex)}>
                              {isSubcolumnActive ? "Collapse" : "Expand"} Subcolumn
                            </button>
                            <div className="subcolumn-posts">
                              {isSubcolumnActive && renderPosts(subCol, index, subIndex)}
                            </div>
                          </div>
                        );
                      })}

                    <div style={{ textAlign: "right", marginTop: "8px" }}>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        style={{
                          backgroundColor: "#007BFF",
                          color: "white",
                          fontSize: "12px",
                          padding: "5px 10px",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                <div className={`posts-wrapper column-${index}`}>
                  {renderPosts(column, index, isMainColumn ? null : colIndex)}

                  {/* Show "Load More" button only if there are posts and hasMore is true */}
                  {isMainColumn && hasMore && column.posts.length > 0 && !loading ? (
                    <button onClick={() => handleLoadMore(index)} className="load-more-button" title="Load More Posts">
                      <FontAwesomeIcon icon={faChevronDown} className="load-more-icon" />
                    </button>
                  ) : null}

                  {/* Show loading indicator */}
                  {isMainColumn && loading && <div className="loading-indicator">Loading...</div>}
                </div>
              </div>
            );
          })}
              </div>
          );
        })}
        <div className="add-column-wrapper" style={{ zIndex: 30 }}>
          <button
            className="add-column-button"
            data-tooltip-id="add-column-tooltip"
            data-tooltip-content="Click to Add New Column"
            onClick={handleAddColumn}
            disabled={isLoading} // ✅ Disable button while loading
          >
            {isLoading ? "Loading..." : "+"}
          </button>

          <Tooltip id="add-column-tooltip" place="top" />

          {/* ✅ Show a spinner while loading */}
          {isLoading && <div className="spinner"></div>}
        </div>
      </div>
    </div>
  );
};

export default ContentArea;
